import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import EmptyState from '@/components/ui/EmptyState';
import Lightbox from '@/components/ui/Lightbox';
import SectionHeader from '@/components/ui/SectionHeader';
import type { ProductionState } from '@/hooks/useProduction';
import { thumbSrc } from '@/lib/assetKeys';

/**
 * Footage productions: the real clips pulled from Iconik that the edit will be
 * assembled from. Nothing here is generated — these are the rushes.
 */
export default function SourceFootage({ assets, productionId }: { assets: ProductionState['assets']; productionId?: string }) {
  const { t } = useTranslation('production');
  const clips = assets
    .filter((a) => a.type === 'footage')
    .sort((a, b) => a.shot_id.localeCompare(b.shot_id, undefined, { numeric: true }));
  const [enlarged, setEnlarged] = useState<{ src: string; label: string } | null>(null);
  const [videoFailed, setVideoFailed] = useState(false);

  // The ingest dual-writes a 720p-class review preview per clip
  // (footage/preview/<stem>.mp4 next to footage/thumbs/<stem>.png).
  const previewSrc = (thumbPath: string) =>
    thumbPath.replace('/footage/thumbs/', '/footage/preview/').replace(/\.png(\?.*)?$/, '.mp4');

  if (clips.length === 0 || !productionId) {
    return <EmptyState title={t('footage.emptyTitle')}>{t('footage.empty')}</EmptyState>;
  }

  return (
    <div>
      <SectionHeader title={t('step.source')} help={t('footage.help')} subtitle={t('footage.count', { count: clips.length })} />
      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-3">
        {clips.map((c) => (
          <button
            key={c.shot_id}
            type="button"
            className="relative overflow-hidden rounded-lg border border-border text-left transition-colors hover:border-primary/50"
            onClick={() => {
              setVideoFailed(false);
              setEnlarged({ src: c.path, label: c.shot_id });
            }}
          >
            <img src={thumbSrc(c.path)} alt={c.shot_id} className="w-full" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="rounded-full bg-black/50 p-2 text-white">▶</span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 truncate bg-black/60 px-1.5 py-1 font-mono text-[9px] text-white">
              {c.shot_id}
            </div>
          </button>
        ))}
      </div>

      {enlarged && (
        <Lightbox label={enlarged.label} onClose={() => setEnlarged(null)}>
          {videoFailed ? (
            // Older productions never uploaded a preview copy — fall back to the still.
            <img
              src={enlarged.src}
              alt={enlarged.label}
              className="mx-auto max-h-[75vh] w-auto max-w-full rounded-lg object-contain"
            />
          ) : (
            <video
              src={previewSrc(enlarged.src)}
              poster={enlarged.src}
              controls
              autoPlay
              className="w-full rounded-lg"
              onError={() => setVideoFailed(true)}
            />
          )}
        </Lightbox>
      )}
    </div>
  );
}
